import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./HPLogs.scss";
import { GetAllPatientDetailsHealthProfessional } from "../../../utils/apiCalls/patients";
import { GetAllDeviceHealthProfessional } from "../../../utils/apiCalls/devices";
import HPLogsHeader from "./HPLogsHeader";
import HPLogsUserTable from "./HPLogsUserTable";
import HPLogsDeviceTable from "./HPLogsDeviceTable";

// Persisted across navigating to a user/device activity detail page and
// back, so the search/page isn't silently reset on return.
const SEARCH_KEY = "hpLogsSearch";
const USER_PAGE_KEY = "hpLogsUserPage";
const DEVICE_PAGE_KEY = "hpLogsDevicePage";

const HPLogs = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [tab, setTab] = useState(() => {
    const activeTabFromState = location.state?.activeTab;
    return activeTabFromState === "devices" ? "devices" : "users";
  });

  // Users state
  const [isLoading, setIsLoading] = useState(true);
  const [userList, setUserList] = useState([]);
  const [page, setPage] = useState(() => Number(sessionStorage.getItem(USER_PAGE_KEY)) || 0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [totalRows, setTotalRows] = useState(0);
  const [userFetchError, setUserFetchError] = useState("");

  // Devices state
  const [isDeviceLoading, setIsDeviceLoading] = useState(true);
  const [deviceList, setDeviceList] = useState([]);
  const [devicePage, setDevicePage] = useState(() => Number(sessionStorage.getItem(DEVICE_PAGE_KEY)) || 0);
  const [deviceRowsPerPage, setDeviceRowsPerPage] = useState(100);
  const [deviceTotalRows, setDeviceTotalRows] = useState(0);
  const [deviceFetchError, setDeviceFetchError] = useState("");

  // 🔍 Single search for both tables (but used conditionally by tab)
  const [searchQuery, setSearchQuery] = useState(() => sessionStorage.getItem(SEARCH_KEY) || "");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(() => sessionStorage.getItem(SEARCH_KEY) || "");
  const debounceTimeoutRef = useRef(null);
  // Restoring a persisted search on mount shouldn't reset the persisted page.
  const isFirstSearchRun = useRef(true);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    sessionStorage.setItem(USER_PAGE_KEY, String(newPage));
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeviceChangePage = (event, newPage) => {
    setDevicePage(newPage);
    sessionStorage.setItem(DEVICE_PAGE_KEY, String(newPage));
  };

  const handleDeviceChangeRowsPerPage = (event) => {
    setDeviceRowsPerPage(parseInt(event.target.value, 10));
    setDevicePage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Change tab and reset pagination for that tab
  const handleTabChange = (nextTab) => {
    setTab(nextTab);

    // 🔹 Clear search when switching tabs
    setSearchQuery("");
    setDebouncedSearchQuery("");
    sessionStorage.setItem(SEARCH_KEY, "");

    // 🔹 Reset pagination for the tab you’re going to
    if (nextTab === "users") {
      setPage(0);
      sessionStorage.setItem(USER_PAGE_KEY, "0");
    } else {
      setDevicePage(0);
      sessionStorage.setItem(DEVICE_PAGE_KEY, "0");
    }
  };

  const fetchAllPatientDetails = useCallback(
    async (pageIndex, pageSize, searchString) => {
      setIsLoading(true);
      setUserFetchError("");
      const payload = {
        pageIndex: pageIndex,
        pageSize: pageSize,
        searchString: searchString ?? "",
        selectedDate: null,
      };

      try {
        const res = await GetAllPatientDetailsHealthProfessional(payload);
        const { data, message, statusCode } = res;

        if (statusCode === 200) {
          setUserList(data);
          setTotalRows(res.totalRecords || data.length);
        } else if (statusCode === 204) {
          console.warn("No data found:", message);
          setUserList([]);
          setTotalRows(0);
        } else {
          console.error("Error fetching patient by ID:", message);
          setUserList([]);
          setTotalRows(0);
          setUserFetchError(message || "Failed to load users");
        }
      } catch (error) {
        console.error("Error fetching patient by ID:", error?.message);
        setUserList([]);
        setTotalRows(0);
        setUserFetchError(error?.message || "Failed to load users");
      }

      setIsLoading(false);
    },
    []
  );

  const fetchAllDevices = useCallback(
    async (pageIndex, pageSize, searchString) => {
      setIsDeviceLoading(true);
      setDeviceFetchError("");
      const payload = {
        pageIndex: pageIndex,
        pageSize: pageSize,
        searchString: searchString ?? "",
        selectedDate: null,
      };

      try {
        const res = await GetAllDeviceHealthProfessional(payload);
        const { data, message, statusCode } = res;

        if (statusCode === 200) {
          setDeviceList(data);
          setDeviceTotalRows(res.totalRecords || data.length);
        } else if (statusCode === 204) {
          console.warn("No devices found:", message);
          setDeviceList([]);
          setDeviceTotalRows(0);
        } else {
          console.error("Error fetching devices:", message);
          setDeviceList([]);
          setDeviceTotalRows(0);
          setDeviceFetchError(message || "Failed to load devices");
        }
      } catch (error) {
        console.error("Error fetching devices:", error?.message);
        setDeviceList([]);
        setDeviceTotalRows(0);
        setDeviceFetchError(error?.message || "Failed to load devices");
      }

      setIsDeviceLoading(false);
    },
    []
  );

  // 🔁 Debounce single search input
  // Only resets pagination for the ACTIVE tab
  useEffect(() => {
    clearTimeout(debounceTimeoutRef.current);
    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      sessionStorage.setItem(SEARCH_KEY, searchQuery);
      if (isFirstSearchRun.current) {
        isFirstSearchRun.current = false;
      } else if (tab === "users") {
        setPage(0);
      } else {
        setDevicePage(0);
      }
    }, 500);

    return () => clearTimeout(debounceTimeoutRef.current);
  }, [searchQuery, tab]);

  // Fetch user data whenever pagination/search changes AND tab is 'users'
  useEffect(() => {
    if (tab === "users") {
      fetchAllPatientDetails(page, rowsPerPage, debouncedSearchQuery);
    }
  }, [tab, page, rowsPerPage, debouncedSearchQuery, fetchAllPatientDetails]);

  // Fetch device data whenever pagination/search changes AND tab is 'devices'
  useEffect(() => {
    if (tab === "devices") {
      fetchAllDevices(devicePage, deviceRowsPerPage, debouncedSearchQuery);
    }
  }, [
    tab,
    devicePage,
    deviceRowsPerPage,
    debouncedSearchQuery,
    fetchAllDevices,
  ]);

  const retryUserFetch = () => {
    fetchAllPatientDetails(page, rowsPerPage, debouncedSearchQuery);
  };

  const retryDeviceFetch = () => {
    fetchAllDevices(devicePage, deviceRowsPerPage, debouncedSearchQuery);
  };

  const handleRowClick = (user) => {
    navigate(`/user-activity-logs/${user.userId}`, {
      state: {
        user,
        fromTab: "users",
      },
    });
  };

  const handleDeviceRowClick = (device) => {
    navigate(`/device-activity-logs/${encodeURIComponent(device.deviceName)}`, {
      state: {
        device,
        fromTab: "devices",
      },
    });
  };

  const searchPlaceholder =
    tab === "users" ? "Search by User Name" : "Search by Device Name";

  return (
    <div className="logs_container">
      <HPLogsHeader
        tab={tab}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        searchPlaceholder={searchPlaceholder}
        onTabChange={handleTabChange}
      />

      {tab === "users" && (
        <HPLogsUserTable
          userList={userList}
          isLoading={isLoading}
          userFetchError={userFetchError}
          onRetry={retryUserFetch}
          onRowClick={handleRowClick}
          totalRows={totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}

      {tab === "devices" && (
        <HPLogsDeviceTable
          deviceList={deviceList}
          isDeviceLoading={isDeviceLoading}
          deviceFetchError={deviceFetchError}
          onRetry={retryDeviceFetch}
          onRowClick={handleDeviceRowClick}
          totalRows={deviceTotalRows}
          rowsPerPage={deviceRowsPerPage}
          page={devicePage}
          onPageChange={handleDeviceChangePage}
          onRowsPerPageChange={handleDeviceChangeRowsPerPage}
        />
      )}
    </div>
  );
};

export default HPLogs;
